import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';

import useErrorManager from "../../hooks/useErrorManager";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

import { Button, Box, Typography } from "@mui/material";

import PageContainer from '../Layout/PageContainer';
import GenericScanHeader from '../Layout/GenericScanHeader';

import { useNotification } from '../../contexts/NotificationContext';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext'; // Import breadcrumb context

const NetworkScheduleScan = () => {
    const { gatewayId } = useParams();
    
    const { setError, clearError, getError } = useErrorManager();
    const { showNotification } = useNotification();
    const { updateBreadcrumb } = useBreadcrumb(); // Use breadcrumb context to update breadcrumb
    
    const navigate = useNavigate();
    const axiosInstance = useAxiosPrivate();

    const [scanDetails, setScanDetails] = useState({
        commonCredentials: true,
        // temporaryForwardingProfileId: 0,
        description: "",
        rightnow: false,
        time: new Date().toISOString(),
        duration: 300,
    });

    const [loading, setLoading] = useState(true);
    const [details, setDetails] = useState(null);

    useEffect(() => {
        const fetchGatewayDetails = async () => {
            try {
                const response = await axiosInstance.get(`/gateways/${gatewayId}/details-tariff`);
                setDetails(response.data);
                setScanDetails((prev) => ({
                    ...prev,
                    description: `${response.data.name} Scan` || "",
                }));
                clearError('fetchGwDetails');
                setLoading(false);

                updateBreadcrumb([
                    { label: 'Manage Gateways', link: '/gateways' },
                    { label: response.data.name || 'Gateway', link: `/gateways/${gatewayId}` }, // Dynamically set location name
                    { label: 'Network Scan', link: null }
                ]);

            } catch (error) {
                setError('fetchGwDetails', error.message);
                setLoading(false);
            }
        };
        
        fetchGatewayDetails();
    }, [gatewayId]);

    const handleScanDetailsChange = (field, value) => {
        setScanDetails((prev) => ({
            ...prev,
            [field]: value, // Dynamically update the specified field
        }));
    };

    const handleCreate = async () => {
        try {
            await axiosInstance.put(`/net/scan/${gatewayId}/plan`, scanDetails);
            clearError('createScan');
            showNotification('Network scan planned successfully', 'success');
            navigate(`/gateways/${gatewayId}`);
        } catch (error) {
            setError('createScan', "There was an error scheduling the network scan. Please try again later.");
        }
    };

    const handleCancel = () => {
        navigate(`/gateways/${gatewayId}`);
    };

    // const scanInfoRows = {
    //     duration: { 
    //         label: "Duration", 
    //         value: `${Math.floor(scanDetails.duration/60)} Minutes` 
    //     },
    //     nextConnection: { 
    //         label: "Next Gateway Connection", 
    //         value: details?.nextConnection 
    //             ? new Intl.DateTimeFormat('it-EU', { 
    //                 day: '2-digit', 
    //                 month: '2-digit', 
    //                 year: 'numeric', 
    //                 hour: '2-digit', 
    //                 minute: '2-digit',
    //                 hour12: false 
    //             }).format(new Date(details.nextConnection))
    //             : 'unknown',
    //         rawValue: details?.nextConnection ? new Date(details.nextConnection) : null
    //     },
    // };
    const infoRows = [
        { key: "duration", label: "Duration", render: (item) =>  `${Math.floor(item.duration/60)} Minutes`   },
        { key: "nextConn", label: "Next Gateway Connection", 
            render: (item) =>  
                    item.gwDetails?.nextConnection 
                ? 
                    new Intl.DateTimeFormat('it-EU', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric', 
                        hour: '2-digit', 
                        minute: '2-digit',
                        hour12: false 
                    }).format(new Date(item.gwDetails.nextConnection))
                : 'unknown',  
        },
        { key: "networkScan", label: "Networks Scans", render: (item) => `${item.gwDetails.tariffInfo.usedNetworkScansPerYear} used (Max ${item.gwDetails.tariffInfo.networkScansPerYear})` },
    ];
    
    return (
        <PageContainer>
            {!loading && details ? (
                <>
                    <GenericScanHeader
                        scanDetails={{...scanDetails, gwDetails: details}}
                        infoRows={infoRows}
                        onScanDetailsChange={handleScanDetailsChange}
                    />
                    <Box display="flex" justifyContent="flex-start" gap={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCreate}
                            disabled={
                                !scanDetails.rightnow && (
                                    new Date(scanDetails.time) < new Date()|| 
                                    new Date(scanDetails.time) < new Date(details?.nextConnection || new Date())
                                )
                            }
                        >
                            Create
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleCancel}
                        >
                            Cancel
                        </Button>
                    </Box>
                    {getError('createScan') && <Typography color="error" variant="title2">{getError('createScan')}</Typography>}
                </>
            ) : (
                <p>Loading...</p>
            )}
        </PageContainer>
    );
};

export default NetworkScheduleScan;
